import { useOrganization } from 'src/context/organization-context';
import { useBillingControllerGetFigmaPlugin } from 'src/apis/backend/generated/billing/billing';

// ----------------------------------------------------------------------

/**
 * Whether this brand may build its own emails in the app.
 *
 * The question used to be an organization-wide feature flag, which was the
 * wrong shape for it: what a brand can do here is what it is billed for, and an
 * agency holds Base brands and managed brands side by side in one organization.
 * A flag could only answer for all of them at once, so it answered wrong for
 * whichever half it was not set for.
 *
 * Base is the plan that buys the builder, so the answer is the tier and nothing
 * else. `white_glove` is deliberately not included: that brand's emails are
 * made by our team, and offering it the builder alongside them is offering a
 * second product it did not buy. `figma_only` and `free` never had it.
 *
 * Read from the org's billing status, which is the only place the web can see a
 * brand's tier -- one org-scoped query shared with the billing screens and the
 * White Glove gates, so asking here costs a cache read rather than a request.
 *
 * Fails closed. An absent tier is unknown rather than permissive: the brand may
 * not be in the listing yet, or may not be in this org at all, and opening a
 * doorway that then refuses is worse than opening it a moment late. Callers
 * that would otherwise redirect on the closed answer should wait on
 * `isLoading`, so a Base brand never bounces off its own page on first paint.
 */
export function useSelfServeAccess(): { enabled: boolean; isLoading: boolean } {
  const { activeOrganization, activeClient } = useOrganization();
  const orgId = activeOrganization?.id ?? '';

  const { data, isLoading } = useBillingControllerGetFigmaPlugin(orgId, {
    query: { enabled: !!orgId, staleTime: 300_000, retry: false },
  });

  const tier = data?.brands?.find((entry) => entry.clientId === activeClient?.id)?.tier;

  return { enabled: tier === 'full', isLoading };
}
